import { apiFetch } from './client';

export type ServiceStatus = 'operational' | 'degraded' | 'down' | 'unknown';

export interface ServiceHealth {
  id: string;
  name: string;
  category: string;
  status: ServiceStatus;
  /** Live readiness/liveness probe round-trip in ms. */
  responseMs: number | null;
  /** Reason a service is degraded/down (e.g. "database: degraded"). */
  reason: string | null;
  /** Per-dependency readiness checks, when the service reports them. */
  checks: Record<string, string> | null;
  /** Grafana service.name template value for the dashboard deep link. */
  grafanaService: string;
  // ── Phase 2 (Prometheus) — null until metrics are wired ──
  uptime: number | null;
  latencyMs: number | null;
  errorRate: number | null;
  requestsPerMin: number | null;
}

export interface ObservabilitySnapshot {
  services: ServiceHealth[];
  /** Infrastructure dependencies (exporter-derived; may be "unknown"). */
  infrastructure: ServiceHealth[];
  summary: {
    total: number;
    operational: number;
    degraded: number;
    down: number;
    unknown: number;
    worst: ServiceStatus;
  };
  updatedAt: string;
  stale: boolean;
}

export const listServiceHealth = async (
  signal?: AbortSignal,
): Promise<ObservabilitySnapshot> => {
  return apiFetch<ObservabilitySnapshot>('/observability/services', { signal });
};

/** A single monitored instance behind an infrastructure dependency. */
export interface InfraInstance {
  id: string;
  name: string;
  status: ServiceStatus;
  reason: string | null;
}

/** Per-instance drill-down payload for one infrastructure dependency. */
export interface InfraInstances {
  id: string;
  name: string;
  /** Grafana dashboard uid for deep links, when provisioned. */
  dashboardUid: string | null;
  /** Grafana template variable that filters the dashboard to one instance. */
  dashboardVar: string | null;
  /** False when the portal's Prometheus URL is not configured. */
  metricsConfigured: boolean;
  instances: InfraInstance[];
}

export const listInfraInstances = async (
  id: string,
  signal?: AbortSignal,
): Promise<InfraInstances> => {
  return apiFetch<InfraInstances>(
    `/observability/infrastructure/${encodeURIComponent(id)}/instances`,
    { signal },
  );
};

export type SeriesMetric = 'latency' | 'errorRate' | 'rps';

export interface SeriesPoint {
  /** Unix epoch seconds. */
  t: number;
  /** Sample value, or null where Prometheus reported no data for the step. */
  v: number | null;
}

export interface ServiceSeries {
  metric: SeriesMetric;
  window: string;
  /** Unit of the values ("ms" | "%" | "req/min"). */
  unit: string;
  points: SeriesPoint[];
}

export const getServiceSeries = async (
  serviceId: string,
  metric: SeriesMetric,
  window = '24h',
  signal?: AbortSignal,
): Promise<ServiceSeries> => {
  return apiFetch<ServiceSeries>(
    `/observability/services/${encodeURIComponent(serviceId)}/series?metric=${metric}&window=${window}`,
    { signal },
  );
};
